import { useState, useEffect } from "react";
import { api, apiError, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CITIES, districtsFor } from "@/lib/categories";
import { USTA_TITLES } from "@/lib/titles";
import { Loader2, X, Plus, Check } from "lucide-react";
import { toast } from "sonner";

export function UstaProfileForm({ initial, onSaved }) {
  const { refreshUser } = useAuth();
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    avatar: initial?.avatar || "",
    specialty: initial?.specialty || "",
    extra_titles: initial?.extra_titles || [],
    city: initial?.city || "",
    categories: initial?.categories || [],
    service_areas: initial?.service_areas || [],
    portfolio: initial?.portfolio || [],
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const toggleCat = (slug) => {
    setForm((f) => ({ ...f, categories: f.categories.includes(slug) ? f.categories.filter((c) => c !== slug) : [...f.categories, slug] }));
  };

  const toggleExtra = (title) => {
    setForm((f) => {
      if (f.extra_titles.includes(title)) return { ...f, extra_titles: f.extra_titles.filter((t) => t !== title) };
      if (f.extra_titles.length >= 3) { toast.warning("En fazla 3 ek uzmanlık seçebilirsiniz"); return f; }
      return { ...f, extra_titles: [...f.extra_titles, title] };
    });
  };

  const toggleArea = (d) => {
    setForm((f) => ({ ...f, service_areas: f.service_areas.includes(d) ? f.service_areas.filter((x) => x !== d) : [...f.service_areas, d] }));
  };

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const url = await uploadFile(f, "profile"); setForm((s) => ({ ...s, avatar: url })); toast.success("Fotoğraf yüklendi — admin onayından sonra görünür"); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };
  const uploadPortfolio = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) { const url = await uploadFile(f, "portfolio"); setForm((s) => ({ ...s, portfolio: [...s.portfolio, url] })); }
      toast.success("Görsel(ler) yüklendi — admin onayından sonra vitrinde görünür");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.specialty) { toast.error("Lütfen bir ana unvan seçin"); return; }
    const extra = form.extra_titles.filter((t) => t !== form.specialty).slice(0, 3);
    setSaving(true);
    try {
      await api.put("/ustalar/me", { ...form, extra_titles: extra });
      await refreshUser();
      toast.success("Usta profiliniz kaydedildi");
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const districts = form.city ? districtsFor(form.city) : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <img src={form.avatar || "https://ui-avatars.com/api/?name=U&background=D1FAE5&color=059669"} alt="" className="h-16 w-16 rounded-full object-cover border" />
        <div className="flex-1">
          <Label className="text-sm">Profil Fotoğrafı</Label>
          <input type="file" accept="image/*" onChange={uploadAvatar} className="block w-full text-sm mt-1 file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-50 file:text-emerald-700 file:px-3 file:py-1.5 file:font-semibold" data-testid="usta-form-avatar-upload" />
          <p className="text-[11px] text-amber-600 mt-1 leading-relaxed">Fotoğrafınız yüklendikten sonra yönetici onayına gönderilir; onaylanana kadar vitrininizde boş avatar görünür.</p>
        </div>
      </div>

      <div>
        <Label className="text-sm">Ana Unvan <span className="text-red-500">*</span></Label>
        <Select value={form.specialty} onValueChange={(v) => setForm({ ...form, specialty: v, extra_titles: form.extra_titles.filter((t) => t !== v) })}>
          <SelectTrigger className="rounded-xl mt-1" data-testid="usta-form-specialty"><SelectValue placeholder="Unvan seçin" /></SelectTrigger>
          <SelectContent className="max-h-64">{USTA_TITLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-xs text-slate-400 mt-1">Vitrin başlığınızda ve usta kartlarında bu unvan görünür.</p>
      </div>

      <div>
        <Label className="text-sm">Ek Uzmanlıklar (en fazla 3) — {form.extra_titles.length}/3</Label>
        <div className="flex flex-wrap gap-2 mt-2 max-h-48 overflow-y-auto p-2 rounded-xl border border-slate-100 bg-slate-50/50" data-testid="usta-form-extra-titles">
          {USTA_TITLES.filter((t) => t !== form.specialty).map((t) => {
            const active = form.extra_titles.includes(t);
            return (
              <button key={t} type="button" onClick={() => toggleExtra(t)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1 ${active ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>
                {active && <Check className="h-3 w-3" />}{t}
              </button>
            );
          })}
        </div>
      </div>

      <div><Label className="text-sm">Şehir</Label>
        <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v, service_areas: [] })}>
          <SelectTrigger className="rounded-xl mt-1" data-testid="usta-form-city"><SelectValue placeholder="Şehir seçin" /></SelectTrigger>
          <SelectContent className="max-h-64">{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select></div>

      <div><Label className="text-sm">Hizmet Kategorileri</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {cats.map((c) => (
            <button key={c.slug} type="button" onClick={() => toggleCat(c.slug)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${form.categories.includes(c.slug) ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}>
              {c.name}
            </button>
          ))}
        </div></div>

      <div><Label className="text-sm">Hizmet Verdiği İlçeler/Bölgeler</Label>
        {!form.city ? (
          <p className="text-xs text-slate-400 mt-1">Önce şehir seçin.</p>
        ) : (
          <div className="flex flex-wrap gap-2 mt-2">
            {districts.map((d) => (
              <button key={d} type="button" onClick={() => toggleArea(d)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border flex items-center gap-1 ${form.service_areas.includes(d) ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}>
                {form.service_areas.includes(d) && <X className="h-3 w-3" />}{d}
              </button>
            ))}
          </div>
        )}
      </div>

      <div><Label className="text-sm">Portföy Görselleri</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {form.portfolio.map((p, i) => (
            <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden"><img src={p.image || p} alt="" className="w-full h-full object-cover" /><button onClick={() => setForm({ ...form, portfolio: form.portfolio.filter((_, idx) => idx !== i) })} className="absolute top-0 right-0 bg-black/50 text-white rounded-bl p-0.5"><X className="h-3 w-3" /></button></div>
          ))}
          <label className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 cursor-pointer hover:border-emerald-400" data-testid="usta-form-portfolio-upload">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
            <input type="file" accept="image/*" multiple onChange={uploadPortfolio} className="hidden" />
          </label>
        </div></div>
      <Button onClick={save} disabled={saving} className="w-full brand-gradient text-white rounded-xl h-11 font-semibold" data-testid="usta-form-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet ve Usta Ol"}
      </Button>
    </div>
  );
}
